/** Litet API-lager: JSON in och ut, svenska felmeddelanden från servern. */

export class ApiError extends Error {
  status: number;
  code: string;
  fields: Record<string, string>;
  constructor(status: number, message: string, code = 'error', fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new ApiError(0, 'Det gick inte att nå Aurora. Kontrollera din internetanslutning.', 'network');
  }
  const type = res.headers.get('content-type') ?? '';
  const data = type.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.error ?? 'Något gick fel. Försök igen.',
      data?.code ?? 'error',
      data?.fields ?? {},
    );
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  patch: <T>(url: string, body: unknown = {}) => request<T>('PATCH', url, body),
  put: <T>(url: string, body: unknown = {}) => request<T>('PUT', url, body),
  delete: <T>(url: string) => request<T>('DELETE', url),
};

// ---------- Typer som delas av sidorna ----------

export type Segment =
  | { type: 'html'; html: string }
  | { type: 'youtube'; id: string }
  | { type: 'audio'; src: string }
  | {
      type: 'box';
      variant: 'visste-du' | 'reflektion' | 'fordjupning' | 'transkript';
      title: string | null;
      html: string;
    }
  | { type: 'chapters'; items: { seconds: number; label: string }[] };

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  municipality: string;
  school: string;
  schoolForm: string;
  subject: string;
  role: 'participant' | 'teacher' | 'admin';
  emailVerified: boolean;
  pendingEmail: string | null;
  emailNotifications: boolean;
  twoFactorEnabled: boolean;
  /** Lärare som måste slå på tvåstegsverifiering innan lärarsidorna fungerar. */
  twoFactorRequired?: boolean;
  /** Roller per kurs: rättare och kommunkontakt. */
  staff?: {
    courseId: number;
    courseTitle: string;
    role: 'grader' | 'municipality_contact';
    municipality: string | null;
  }[];
}

export interface Progress {
  required: number;
  completed: number;
  percent: number;
  allDone: boolean;
}

export interface CourseItemSummary {
  id: number;
  title: string;
  kind: string;
  required: boolean;
  completed: boolean;
  estimatedMinutes: number | null;
}

export interface CourseOverview {
  course: { id: number; title: string; description: Segment[]; imagePath: string | null };
  cohort: { id: number; name: string };
  progress: Progress;
  resumeItemId: number | null;
  certificate: { code: string; issuedAt: string } | null;
  knowledgeCheck: {
    pre: { itemId: number; title: string; percent: number | null } | null;
    post: { itemId: number; title: string; percent: number | null } | null;
  } | null;
  achievements: {
    key: 'explorer' | 'reflective' | 'classroom' | 'conversation';
    label: string;
    description: string;
    earned: boolean;
    progress: string;
  }[];
  modules: {
    id: number;
    title: string;
    intro: Segment[];
    locked: boolean;
    unlockAt: string | null;
    lockReason: 'date' | 'previous' | null;
    completed: number;
    required: number;
    minutes: number;
    items: CourseItemSummary[];
  }[];
}

export function formatDate(value: string | Date | null | undefined, withTime = false): string {
  if (!value) return '–';
  return new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'medium',
    ...(withTime ? { timeStyle: 'short' } : {}),
    timeZone: 'Europe/Stockholm',
  }).format(new Date(value));
}
